import "@/styles/identityCheck.scss";

import { Spinner } from "@amzn/stencil-react-components/spinner";
import { useContext } from "react";

import { VerificationStatus } from "@/apis/IdentityCheckHelper";
import LightweightIdVerification from "@/components//IdentityCheck/LightweightIdVerification";
import FullIdVerification from "@/components/IdentityCheck/FullIdVerification";
import {
  IVVLegalDisclosure,
  IVVServiceIntegration,
} from "@/components/IdentityCheck/IVV";
import { IVVCompleted } from "@/components/IdentityCheck/IVV/IVVCompleted";
import { IVVFailed } from "@/components/IdentityCheck/IVV/IVVFailed";
import { UnableToVerify } from "@/components/IdentityCheck/UnableToVerify";
import VerificationCompleted from "@/components/IdentityCheck/VerificationCompleted";
import VerificationPending from "@/components/IdentityCheck/VerificationPending";
import { WizardPathChooser } from "@/components/IdentityCheck/WizardPathChooser";
import { useGetWizardEntitlements } from "@/components/IdentityCheck/WizardPathChooser/hooks/useGetWizardEntitlements";
import { useUserProgress } from "@/hooks/useUserProgress";
import { AppContext } from "@/stores/appStore";
import { Day1EventCategory } from "@/types/snowplow-events";

import { useIVVAbort } from "./IVV/hooks/useIVVAbort";

export const DATA_TEST_ID_IDENTITY_CHECK_SPINNER = "IdentityCheckSpinner";
/**
 * This step challenges new hires to verify their identities by taking a video recording and upload it to a S3 bucket.
 * This video recording will be verified by Amazon employees.
 */
export default function IdentityCheck(): JSX.Element {
  const { state } = useContext(AppContext);

  const {
    metadata,
    loading: wizardEntitlementsLoading,
    error: wizardEntitlementsError,
  } = useGetWizardEntitlements();

  const { ivvAborted } = useIVVAbort();

  const { isLoading: isUserProgressLoading, isError: isUserProgressError } =
    useUserProgress({
      enableMetrics: true,
    });
  const isLoading = wizardEntitlementsLoading || isUserProgressLoading;
  const isError = wizardEntitlementsError || isUserProgressError;

  if (isLoading) {
    return <Spinner dataTestId={DATA_TEST_ID_IDENTITY_CHECK_SPINNER} />;
  }

  if (isError) {
    return <UnableToVerify allowAutomatedIdvFallback={false} />;
  }

  /**
   * Choose which identity check workflow is displayed based on currently inflight IDVs and the derived VerificationStatus based on user progress API (@see src/hooks/useUserProgress.ts).
   */
  switch (state.docsVerificationStatus) {
    // Handle case where Automated IDV is successful.
    case VerificationStatus.AUTOMATED_IDV_SUCCESS:
      return <IVVCompleted />;

    // Handle fall back case for IDV rejected. The user should be told and prompted to perform manual IDV instead.
    case VerificationStatus.AUTOMATED_IDV_REJECTED:
      return <IVVFailed failedReason={"REJECTED"} />;

    /**
     * Auto IDV will have an Inflight status if the user previously requested an integration URL (<IVVServiceIntegration /> loaded).
     *
     * If the Auto IDV is inflight, redirect them to the IVVServiceIntegration component where it will attempt to get a new IDV URL,
     * however the IDV from before, if present, will have been cached and returned by the API call and will reload the IVV/persona auto IDV widget.
     *
     * Or, if it's been longer than 10 minutes, API will reject and throw a 400 with the code SUBMISSION_PENDING_VERIFICATION and we end up
     * displaying the Cant Load/fallback options component.
     */
    case VerificationStatus.AUTOMATED_IDV_INFLIGHT:
      if (ivvAborted) {
        return <IVVFailed failedReason={"ABORTED"} />;
      }
      /**
       * Figure out if we locally timed out this before, in the case of a user refreshing the page after timed out.
       */
      if (localStorage.getItem("IVV_TIMED_OUT")) {
        return <IVVFailed failedReason={"TIMED_OUT"} />;
      }

      // Redirect to the service integration component since the user has previously agreed to the legal warning if there is an inflight verification.
      return <IVVServiceIntegration />;

    case VerificationStatus.CONTACT_ITS: {
      return (
        <UnableToVerify
          allowAutomatedIdvFallback={metadata?.isAutomatedIdvEligible}
        />
      );
    }

    // Handle cases where the user has completed verification at all.
    case VerificationStatus.LIGHT_SUCCESS:
    case VerificationStatus.FULL_SUCCESS:
    case VerificationStatus.COMPLETED: {
      return <VerificationCompleted />;
    }

    // This status is only available if the user started the manual IDV process in PD1 Wizard. As such, they will not be asked to perform automated IDV at all.
    case VerificationStatus.LIGHT: {
      return (
        <LightweightIdVerification
          eventCategory={Day1EventCategory.IDCheckLight}
        />
      );
    }

    /**
     * If the user has not started anything yet OR is tagged as being required to perform a FULL verification.
     * If the user has NOT entered the Light verification workflow, both cases where the user CAN complete either Manual or Automated IDV are handled in this condition.
     */
    case VerificationStatus.FULL:
    case VerificationStatus.NOT_STARTED: {
      switch (state.wizardPath.current) {
        case "ManualRIV":
          /**
           * If any IDV is in-flight, immediately display the Pending display.
           * If the IDV has been COMPLETED already, the case VerificationStatus.FULL_SUCCESS is hit and this section is skipped anyways.
           */
          if (metadata?.isManualIdvInFlight) {
            return <VerificationPending />;
          } else {
            // If the customer is eligible for FULL manual IDV and has NOT started it yet.
            return (
              <FullIdVerification
                allowAutomatedIdvFallback={metadata?.isAutomatedIdvEligible}
              />
            );
          }
        case "IVV":
          // If any automated IDV is in-flight, show the Complete page which will handle checking with the back end and unlocking the rest of the wizard.
          if (metadata?.isAutomatedIdvInFlight) {
            if (localStorage.getItem("IVV_TIMED_OUT")) {
              return <IVVFailed failedReason={"TIMED_OUT"} />;
            }

            return <IVVCompleted />;
          } else {
            // If none is in-flight, show the customer the full automated IDV workflow.
            return (
              <IVVLegalDisclosure>
                <IVVServiceIntegration />
              </IVVLegalDisclosure>
            );
          }
        default:
          if (metadata?.isManualIdvInFlight) {
            /**
             * In the case where Manual IDV is already in flight, AND
             * In the case where no wizard path was selected. Such as if the user closed the browser and went back to the wizard URL, they would not have a preference but DID have a submitted IDV.
             */
            return <VerificationPending />;
          } else if (metadata?.isAutomatedIdvInFlight) {
            /**
             * In the case where Automated IDV is already in flight, AND
             * In the case where no wizard path was selected. Such as if the user closed the browser and went back to the wizard URL, they would not have a preference but DID have a submitted IDV.
             */

            /**
             * This handles an edge case where there is a local timed out state that is persisted.
             * In this case, when you load the page, you are directed to the Failed/Timed Out IVV display, you click
             * "Record videos" fallback, then click "Back" on the manual IDV which is handled <here>.
             */
            if (localStorage.getItem("IVV_TIMED_OUT")) {
              return <IVVFailed failedReason={"TIMED_OUT"} />;
            }

            return <IVVCompleted />;
          } else if (metadata?.isAutomatedIdvEligible) {
            /**
             * Display the wizard picker if no wizard path has been selected yet, there is no IDV in progress, and the user is eligible for automated IDV
             */
            return <WizardPathChooser />;
          } else {
            /**
             * If the user has not started an IDV and is not eligible for automated IDV, direct them to manual IDV without the option to fallback to automated IDV.
             */
            return <FullIdVerification allowAutomatedIdvFallback={false} />;
          }
      }
    }
    // LIGHT_PENDING and FULL_PENDING are specific to Manual IDV methods and do not need to worry about Automated RIV logic.
    case VerificationStatus.LIGHT_PENDING:
    case VerificationStatus.FULL_PENDING: {
      return <VerificationPending />;
    }
    default: {
      // Covers failure cases as well as catch all for anything else that might have gone wrong.
      return (
        <UnableToVerify
          allowAutomatedIdvFallback={metadata?.isAutomatedIdvEligible}
        />
      );
    }
  }
}
